/**
 *
 * FaqsPage
 *
 */
import React, { memo } from 'react';
import styled from 'styled-components/macro';
import {TFuncReturn, useTranslation} from 'react-i18next';
import { messages } from './messages';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import {faCheckCircle, faTimesCircle} from "@fortawesome/pro-solid-svg-icons";
import {Grid, useMediaQuery} from "@mui/material";
import { Link } from 'react-router-dom';

interface Props {}

export function TrainingDosAndDonts() {
    return <Grid container>
        <Grid xs={6}>
            <ListOfItems>
                <ColumnTitle style={{color: "#55CDC4"}}>Works with:</ColumnTitle>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faCheckCircle} color={"#55CDC4"}/>
                    <ListItemText className="value-prop">Singing/Rapping/Speaking</ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faCheckCircle} color={"#55CDC4"}/>
                    <ListItemText className="value-prop">
                      iPhone memos <br/>
                      <span style={{fontSize: '14px'}}>
                          (remove bg noise with{' '}
                            <a href="https://tape.it/denoiser" target="_blank">
                                tape.it
                            </a>
                          )
                      </span>
                    </ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faCheckCircle} color={"#55CDC4"}/>
                    <ListItemText className="value-prop">EQ, Light Compression, Pitch Correction to taste</ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faCheckCircle} color={"#55CDC4"}/>
                    <ListItemText className="value-prop">Studio or Bedroom Vocals</ListItemText>
                </ListItemContainer>
            </ListOfItems>
        </Grid>
        <Grid xs={6}>
            <ListOfItems>
                <ColumnTitle style={{color: "#FD6C6E"}}>Does NOT work with:</ColumnTitle>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faTimesCircle} color={"#FD6C6E"}/>
                    <ListItemText className="value-prop">Layered voices</ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faTimesCircle} color={"#FD6C6E"}/>
                    <ListItemText className="value-prop">Reverb and echoes</ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faTimesCircle} color={"#FD6C6E"}/>
                    <ListItemText className="value-prop">Modulation or distortion effects</ListItemText>
                </ListItemContainer>
                <ListItemContainer>
                    <FontAwesomeIcon icon={faTimesCircle} color={"#FD6C6E"}/>
                    <ListItemText className="value-prop">Audio with headphone leak or instruments</ListItemText>
                </ListItemContainer>
            </ListOfItems>
        </Grid>
    </Grid>;
}

export function SwapVoiceFaqsContent(props: { hideTroubleshootingFAQs?: boolean }) {
    const isMobile = useMediaQuery('(max-width: 600px)');

    return <Div>
        <Faqs>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How do I make AI Choirs? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>Experiment with different pitch shifts to create harmonies and full choirs in your voice or any other voice. If the tone sounds off in certain ranges, try blending your voice model with a royalty free voice model to capture elements of their tone (Go to My Voices).</p>
                    <p> Watch this video to learn more about creating AI choirs: </p>
                    <VideoContainer>
                        <iframe width={isMobile ? '300' : '560'} height={isMobile ? '169' : '315'} src="https://www.youtube-nocookie.com/embed/mP7flvz1u_Y?si=GY1VV1vZO1FXNVke" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
                    </VideoContainer>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How do I swap my voice to an instrument? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> Watch this video to learn more about swapping your voice to an instrument: </p>
                    <VideoContainer>
                        <iframe width={isMobile ? '300' : '560'} height={isMobile ? '169' : '315'}  src="https://www.youtube-nocookie.com/embed/-qqs8yz1_Nk?si=bsJ2spOXM6D2mK5E" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
                    </VideoContainer>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How do I swap any song to my voice to make an AI cover? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> Once you've created your voice, you can make an AI cover of any song by pasting a link to the song you want to cover or uploading the song directly. Uploading full songs will always have lower quality than raw vocal recordings because only the lead vocal is being extracted to swap to your voice. This works best on songs with a single lead vocal and not a lot of adlibs or vocal layers. </p>
                    <p> For exclusive songs available on Controlla Voice, you can get ultra high quality covers with all vocal layers swapped to your voice. See how to do this here: </p>
                    <VideoContainer>
                        <iframe width={isMobile ? '300' : '560'} height={isMobile ? '169' : '315'}  src="https://www.youtube.com/embed/OBrKcHjJxLI?si=yWCAGQ6B8V7t93gy" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
                    </VideoContainer>
                </AccordionDetails>
            </Accordion>

            {!props.hideTroubleshootingFAQs && (
                <>
                <Accordion className="faq-accordion">
                    <AccordionSummary
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <h2 className={"faq-title"}> Why does my voice swap output sound distorted or unclear? </h2>
                    </AccordionSummary>
                    <AccordionDetails>
                        <p>The quality of your swap voice output depends on the quality of the input recordings, but can get you results that are indistinguishable from real singers when using raw vocal recordings.</p>
                        <p>
                        Distortion or unclear audio can happen for a few reasons, usually related to extra sounds in the original track or insufficient training data. Here are some tips to help you get the best, cleanest output:
                        <ol>
                            <li>
                                Upload a Clean Lead Vocal Track
                            </li>
                            <p>
                            The best way to achieve high-quality results is to upload an isolated lead vocal track without any background sounds, like instruments or background vocals. If you're uploading a full song instead, with multiple vocal and instrumental layers, make sure to select "Isolate lead vocal from instruments and background vocals" and "Remove Reverb" in the settings. This can help the AI focus on the main vocal, but keep in mind it will not be as clean as uploading a fully isolated track from the start.
                            </p>
                            <li>
                                Retrain your voice model with quality data
                            </li>
                            <p>
                            If you're still experiencing distortion, it might be due to limited or low-quality training data for your voice model. To improve clarity, try adding at least 10 minutes of well-recorded singing across a range of pitches—low, mid, and high tones—to help the AI understand your full vocal range. Make sure these tracks are clean—without background noise or reverb (room echo). Any extra sounds will be learned as part of your voice and can create unwanted artifacts in the output.
                            </p>
                        </ol>
                        </p>
                    </AccordionDetails>
                </Accordion>
                <Accordion className="faq-accordion">
                    <AccordionSummary
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <h2 className={"faq-title"}> How can I improve pronunciation in other languages? </h2>
                    </AccordionSummary>
                    <AccordionDetails>
                        <p>Try setting "Accent Strength" to "Source Accent" in the advanced setting after you upload your input files. This will help the AI replicate the accent of the original singer instead of your voice model's accent. If this doesn't work, you can try retraining your voice model with more data from the language you want to improve.</p>
                    </AccordionDetails>
                </Accordion>

                <Accordion className="faq-accordion">
                    <AccordionSummary
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <h2 className={"faq-title"}> Why does the pitch of my voice swap output sound too high or too low compared to my voice model? </h2>
                    </AccordionSummary>
                    <AccordionDetails>
                        <p>This often happens when the original song's pitch is outside your natural vocal range—like swapping a man's voice with a woman's voice or vice versa. To fix this, try adjusting the Pitch Shift setting after you upload your tracks. Use +12 values to increase the pitch by an octave and -12 values to lower it an octave. </p>
                        <p>Note that currently when swapping a full song, the instrumental is not pitch shifted and will be out of key if you use incorrect values.</p>
                    </AccordionDetails>
                </Accordion>
                </>
            )}

            
        </Faqs>
    </Div>;
}

export function PricingFaqsContent(props) {
    const isMobile = useMediaQuery('(max-width: 600px)');

    return <Div>
        <Faqs>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> What if I don't like my voice? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>The quality of your voice depends on the quality of your training data.</p>
                    <p>If you are not satisfied with your voice, you can reach out to support for feedback on your dataset and a re-do if needed. We're here to help!</p>
                </AccordionDetails>
            </Accordion>
        </Faqs>
    </Div>;
}

// Landing page FAQs
export function FaqsContent(props) {
    return <Div>
        <Faqs>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> What can I do with Controlla Voice? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        With Controlla Voice, you can:
                        <ol>
                            <li> Transform your voice to ultra realistic AI singing voices, instruments and create massive choirs </li>
                            <li>
                                Swap any song to your own voice in any language. Train your own AI Singing Voice that sounds like you
                            </li>
                            <li>
                                Clone or sample entire choirs with 15 seconds of audio to sing lyrics in that choirs style  
                            </li>
                            <li>
                                Clone or sample instrumental styles to generate new instrumental samples
                            </li>
                            <li>
                                Extract stems of any kind and isolate vocals with the highest quality stem-splitter. Extract kick, snare, FX, lead vocals from background vocals and more
                            </li>
                        </ol>
                    </p>
                    <p>
                        This opens up endless possibilities for vocal mixing, sound design, producing, and songwriting.
                    </p>
                    <p>
                        Transform your voice into choirs, Generate AI Cover songs, Sing in different languages, or rap faster than you ever could in real
                        life.
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Who owns the generated vocals?</h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        You own all outputs of vocals you generate with Controlla Voice's tools as long as you own the inputs you upload to create them. You may use them commercially as you wish. 
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How do I train my own AI singing voice? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        <i>
                            Head over to the "My Voices" page and click "Create a Voice". You will be prompted with a
                            form to upload audio files. You need a Starter or Creator Plan to create a voice.
                        </i>
                    </p>
                    <p>
                        1. For the highest quality, Gather 15-30 minutes of dry single layer vocals.
                    </p>
                    <TrainingDosAndDonts/>
                    <p>
                        2. After uploading, click "Create" and you'll get an email when your voice is ready (usually 15 min - 1 hour depending on length of your vocals)
                    </p>
                    <p>
                        3. After your voice is ready, you can create and release songs from our catalog on steaming platforms or convert any link to your voice for practice purposes on the Swap Voice page.
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Who has access to my voice? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        By default, only you have secure private access to your voice model. You can optionally grant
                        access to your team members - once granted they have access to using, and blending your voice.
                        Only grant access to users you trust.
                    </p>
                </AccordionDetails>
            </Accordion>
        </Faqs>
    </Div>;
}

export function SongsFaqsContent(props) {
    return <Div>
        <Faqs>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Can I do songs not listed? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        Yes! You can upload or paste a link to any song you have rights to cover on the <Link to={"/app/generate"}> Swap Voice page </Link>and swap the lead vocal to your voice. For release ready results you can also upload your own raw vocals and swap them to any voice or your own. 
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Where do these songs come from? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        Most AI covers on social media have been created without permission from the original artists. Controlla is the first platform to ethically source and secure licenses for popular songs so you can AI cover them in your voice.
                    </p>
                    <p>
                        We pay royalties to songwriters and singers involved with both the original and cover of a song so you can get copyright free AI music in your voice
                    </p>
                    <p>
                        This results in the highest quality AI covers ever created and even allows you to release your tracks to streaming platforms so you can jam out to them alongside all your favorite music.
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How can I release my song to Spotify?</h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        Make sure to follow our guide for <a href="https://www.controlla.xyz/post/how-to-release-ai-covers-to-spotify-apple-music-and-all-streaming-platforms" target="_blank">Releasing AI Covers to Streaming</a> and check all the boxes for "This is a Cover Song" when distributing with platforms such as DistroKid. Personal Use licenses have a streaming cap of 50,000 streams (which can earn you back 40x the cost).
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Can I sample or remix my AI cover? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        You can sample and remix the songs in your voice however you like so long as you abide by cover song laws in your jurisdiction. Don't forget to check the box for "Cover Song" when distributing to streaming platforms. This ensures the original songwriters get paid per stream.
                    </p>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How long does it take to create a song? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>
                        If you've already created your voice, you can preview songs instantly and generate full versions in two clicks. Your final mixed song will be ready in your library within 5 - 10 minutes.
                    </p>
                </AccordionDetails>
            </Accordion>
        </Faqs>
        {/*  {t(...messages.someThing())}  */}
    </Div>;
}

export function StyleSamplerFaqsContent(props) {
    const isMobile = useMediaQuery('(max-width: 600px)');

    return <Div>
        <Faqs>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> What kind of audio should I upload as a style? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p>For the style sampler, we recommend just 30 seconds of fully mixed vocals, choirs, or music with all audio effects you would normally use. This ensures your sampled outputs have the same mixing characteristics such as doubles, harmonies, reverb, distortion, etc as the original input. </p>
                    <p>Unlike the swap voice page, the style sampler captures the nuances of your mixing chain, meaning if you have a vocal style that depends on various effects, you can accurately generate new lyrics in that style (with the tradeoff that you have less control over the melody) </p>
                </AccordionDetails>
            </Accordion>

            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> How do I turn my lyrics into any choir </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> With just 15-30 seconds of audio of any choir, fully mixed with effects, you can make that choir sing any lyrics you want. A choir can be a group of singers, or your own stacked vocal layers with adlibs and effects. </p>
                    <p> You can learn how to turn lyrics into choirs and creative ways to use them below:</p>
                    <VideoContainer>
                        <iframe width={isMobile ? '300' : '560'} height={isMobile ? '169' : '315'}  src="https://www.youtube-nocookie.com/embed/C6YOfkznzi4?si=_IrrRr7s6Wy78vO1" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
                    </VideoContainer>
                </AccordionDetails>
            </Accordion>
            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> What languages are supported? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> 
                        The following languages are currently supported:
                        <ul>
                        <li>Chinese</li>
                        <li>Cantonese</li>
                        <li>English</li>
                        <li>Spanish</li>
                        <li>French</li>
                        <li>Russian</li>
                        <li>German</li>
                        <li>Portuguese</li>
                        <li>Arabic</li>
                        <li>Italian</li>
                        <li>Japanese</li>
                        <li>Korean</li>
                        <li>Indonesian</li>
                        <li>Vietnamese</li>
                        <li>Turkish</li>
                        <li>Dutch</li>
                        <li>Ukrainian</li>
                        </ul>
                    </p>
                    <p>We are constantly growing this list so reach out to hello@controlla.xyz if you would like to use it with a different language that's not available.</p>
                </AccordionDetails>
            </Accordion>

            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Where can I find vocal or choirs samples to use as styles? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> You can extract vocal references from your own songs by uploading them to the Stem Splitter page and selecting "All Vocals", "Lead Vocals", or "Background Vocals" depending on your song.</p>
                    <p> You can also use any vocal sample packs, or royalty free acapellas, as long as you have rights to remix them for your specific use. </p>
                </AccordionDetails>
            </Accordion>

            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> What can others do if I share my style publicly with the community? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> If you share your style publicly, in our current early release, anyone can use it to generate any lyrics with no censorship, and use those samples commercially as they wish. </p>
                    <p> The benefit of sharing your styles in this way, is that you will also be able to see all outputs generated from your style, and have access to using them yourself as well. </p>
                    <p> In the future, we plan to allow for more granular controls, collaboration, and monetization from sharing and using each others styles. Reach out to hello@controlla.xyz if you have a use case in mind!  </p> 
                </AccordionDetails>
            </Accordion>

            <Accordion className="faq-accordion">
                <AccordionSummary
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <h2 className={"faq-title"}> Do I need to clear these samples? </h2>
                </AccordionSummary>
                <AccordionDetails>
                    <p> If you are using a copyrighted song, we believe it is best to clear that sample regardless of how much you distort it, if you plan to use it commercially. </p>
                    <p> If you are using your own songs privately, you are free to use the outputs however you wish, controlla does not claim any ownership. </p>
                    <p> If you are using a public community style, the owner of that style can see and use your outputs, however you maintain ownership of the lyrics and any derivatives you create from those samples. </p>
                </AccordionDetails>
            </Accordion>
            
        </Faqs>
    </Div>;
}


export const FaqsPage = memo((props: Props) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();

  return (
    <Page>
        <h1>
            {t('faq-title')}
        </h1>
        <FaqsContent />
    </Page>
  );
});

const Page = styled.div`
  width: 100vw;
  height: calc(100vh - 75px);
  overflow-y: scroll;
`;
const Div = styled.div`
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const Faqs = styled.div`
  .faq-accordion {
    background: #180d23;
    justify-content: center;
    padding: 10px;
    transition: all 0.5s ease;
    
    &:hover {
      
      h2.faq-title {
        color: #ff74e9;
      }
    }
  }
  
  
  h2.faq-title {
    margin: 5px;
    margin-top: 5px !important;
    font-weight: normal;
    font-size: 24px;
    padding: 0px;
    text-align: left;
    line-height: 1.3em;
  }

  p {
    margin: 10px;
    font-size: 20px;
  }
  
  a {
    color: #ff74e9;
  }

  .value-prop {
    color: #f5e1febd;
    font-family: "Jura";
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: normal;
    margin-top: -2px;
    margin-left: 10px;
    margin-bottom: 0px;
  }
`;

const ListOfItems = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-left: 40px;
  margin-right: 40px;
  margin-top: 40px;
  margin-bottom: 40px;

  @media only screen and (max-width: 600px) {
    font-size: 14px;
    margin-left: 10px;
    margin-right: 10px;
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const ColumnTitle = styled.p`
  color: #ff73e8fc;
  font-family: "Raleway";
  font-size: 22px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: normal;
  margin: 0;
`;

const ListItemContainer = styled.div`
  align-items: flex-start;
  display: flex;
  font-size: 18px;
  gap: 8px;

  .bxs-check-circle {
    height: 24px;
    min-width: 24px;
  }

  @media only screen and (max-width: 600px) {
    font-size: 14px;
    gap: 2px;
  }
`;

const ListItemText = styled.p`
  color: #f5e1febd;
  font-family: "Raleway";
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: normal;
  margin-top: -2px;
  margin-left: 10px;
  margin-bottom: 0px;
  text-align: left;

  @media only screen and (max-width: 600px) {
    font-size: 14px;
  }
  
  a {
    color: #ff74e9 !important;
  }
`;

const VideoContainer = styled.div`
  width: 560px;
  height: 315px;
  position: relative;

  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;

  @media only screen and (max-width: 600px) {
    width: 300px;
    height: 169px;
  }
`;